import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, xeroLink } from '../api';

interface Quote {
  xeroId: string;
  number: string | null;
  contactName: string | null;
  email: string | null;
  status: string | null;
  docDate: string | null;
  total: string | null;
  daysSince: number | null;
  lastRemindedAt: string | null;
  reminderCount: number;
}

function fmtDate(ts: string | null) {
  if (!ts) return '-';
  return new Date(ts).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', year: 'numeric' });
}
function money(t: string | null) {
  const n = Number(t);
  return isNaN(n) ? '' : n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

const STATUSES = ['SENT', 'ACCEPTED', 'DECLINED', 'all'];

function QuoteReminderButtons({ q }: { q: Quote }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const remind = useMutation({
    mutationFn: (dry: boolean) => api.remindQuote(q.xeroId, dry),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      setConfirm(false);
    },
  });
  const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
    background: bg,
    color,
    border: bg === '#fff' ? '1px solid var(--color-border,#ccc)' : 'none',
    borderRadius: 6,
    padding: '5px 10px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  });
  if (!q.email) return <span style={{ fontSize: 12, color: 'var(--color-text-muted,#999)' }}>no email</span>;
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <button style={btn('#fff', '#333')} disabled={remind.isPending} onClick={() => remind.mutate(true)} title="Send to nobody, just log a preview">
        Test
      </button>
      {!confirm ? (
        <button style={btn('var(--color-accent)')} onClick={() => setConfirm(true)}>
          Send reminder
        </button>
      ) : (
        <button style={btn('var(--color-error,#EE2937)')} disabled={remind.isPending} onClick={() => remind.mutate(false)}>
          Confirm email {q.contactName?.split(' ')[0] || 'them'}
        </button>
      )}
      {remind.data && (
        <span style={{ fontSize: 12, color: remind.data.status === 'sent' ? 'var(--color-accent)' : 'var(--color-error,#EE2937)' }}>
          {remind.data.status}
          {remind.data.reason ? ` (${remind.data.reason})` : ''}
        </span>
      )}
    </div>
  );
}

export function Quotes() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const search = (sp.get('q') || '').toLowerCase();
  const [status, setStatus] = useState('SENT');
  const { data, isLoading } = useQuery({ queryKey: ['quotes', status], queryFn: () => api.getQuotes(status) });
  const all: Quote[] = data || [];
  const list = search
    ? all.filter((item) => [item.contactName, item.number, item.email].some((v) => (v || '').toLowerCase().includes(search)))
    : all;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 'var(--spacing-3,16px)' }}>
      <h2 style={{ marginTop: 0 }}>Quotes</h2>
      {search && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, padding: '8px 12px', background: 'var(--color-accent-muted)', borderRadius: 8, fontSize: 13 }}>
          <span>Showing quotes for <strong>{sp.get('q')}</strong></span>
          <button onClick={() => setSp({}, { replace: true })} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', fontWeight: 600 }}>Clear</button>
        </div>
      )}
      <p style={{ color: 'var(--color-text-muted,#666)', marginTop: 0 }}>
        Quotes sent from Xero. Follow up the ones still sitting unanswered with a reminder offering help. Reminders
        respect unsubscribes and record when they were sent.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            style={{
              padding: '6px 14px',
              borderRadius: 999,
              border: '1px solid var(--color-border,#ccc)',
              cursor: 'pointer',
              fontWeight: 600,
              background: status === s ? 'var(--color-accent)' : '#fff',
              color: status === s ? '#fff' : 'var(--color-text,#222)',
            }}
          >
            {s === 'all' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 8, fontWeight: 600 }}>
        {isLoading ? 'Loading…' : `${list.length} quote${list.length === 1 ? '' : 's'}`}
      </div>

      <div style={{ border: '1px solid var(--color-border,#e0e0e0)', borderRadius: 8, overflow: 'hidden' }}>
        {list.map((q, i) => (
          <div
            key={q.xeroId}
            style={{
              display: 'flex',
              gap: 12,
              padding: '10px 14px',
              borderTop: i === 0 ? 'none' : '1px solid var(--color-border,#eee)',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: '1 1 240px' }}>
              <div style={{ fontWeight: 600 }}>
                <button
                  onClick={() => navigate(`/contacts?q=${encodeURIComponent(q.email || q.contactName || '')}`)}
                  title="Open this contact"
                  style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 600, color: 'var(--color-accent)', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {q.contactName || '(no name)'}
                </button>{' '}
                <a href={xeroLink.quote(q.xeroId)} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 400 }}>
                  {q.number} ↗
                </a>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted,#666)' }}>
                sent {fmtDate(q.docDate)}
                {typeof q.daysSince === 'number' ? ` · ${q.daysSince}d ago` : ''} · {money(q.total)}
                {q.status && q.status !== 'SENT' ? ` · ${q.status}` : ''}
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-muted,#666)', minWidth: 120 }}>
              {q.reminderCount > 0 ? `reminded ${fmtDate(q.lastRemindedAt)}` : 'not reminded'}
            </div>
            <QuoteReminderButtons q={q} />
          </div>
        ))}
        {!isLoading && list.length === 0 && (
          <div style={{ padding: 14, color: 'var(--color-text-muted,#666)' }}>No quotes with this status.</div>
        )}
      </div>
    </div>
  );
}
